import type { Subsystem } from '../types';
import { NOAA_CATALOGUE, type NoaaCatalogueEntry } from '@/features/loader/catalog/noaaCatalogue';

/** Published reference values for generic twins. No measured-health values or fictitious model locations. */
export function noaaPayloadReferences(entry: NoaaCatalogueEntry): Subsystem[] {
  const sources = NOAA_CATALOGUE.sources;
  const card = (id: string, label: string, sourceUrl: string, values: [string, string][]): Subsystem => ({
    id: `reference_${id}`,
    label,
    modelOnly: true,
    data: [],
    reference: {
      sourceTitle: 'Published instrument reference',
      sourceUrl,
      checkedAt: entry.checkedAt,
      note: 'Design/reference information; no live telemetry or mapped instrument geometry.',
      values: values.map(([label, value]) => ({ label, value })),
    },
  });
  if (entry.family === 'COSMIC-2')
    return [
      card('tgrs', 'TGRS · radio occultation', sources.cosmicPayload, [
        ['Measurement', 'Atmospheric profiles from GNSS signals'],
      ]),
      card('ivm', 'IVM · ion velocity meter', sources.cosmicPayload, [
        ['Measurement', 'Ion velocity, concentration, composition and temperature'],
      ]),
      card('beacon', 'RF beacon', sources.cosmicPayload, [
        ['Measurement', 'Ionospheric total electron content'],
      ]),
    ];
  if (entry.family === 'DMSP')
    return [
      card('ssmis', 'SSMIS · microwave imager / sounder', sources.ssmis, [
        ['Frequency coverage', '19–183 GHz'],
        ['Swath', '1,707 km'],
        [
          'MiRS products',
          entry.id === 'dmsp_f16'
            ? 'Previously operational (product status)'
            : 'Operational (product status at review)',
        ],
      ]),
      card('ols', 'OLS · Operational Linescan System', sources.dmsp, [
        ['Instrument family', 'Optical imaging'],
        ['Context', 'Installed instrument; current availability is product-specific'],
      ]),
    ];
  if (entry.id === 'solar1')
    return [
      card('swips', 'SWiPS · solar-wind plasma', sources.solar, [
        ['Velocity range', '200–2,500 km/s'],
        ['Cadence', '60 s'],
        ['Peak power', '8.1 W'],
        ['Mass', '5.2 kg'],
      ]),
      card('stis', 'STIS · suprathermal ions', sources.solar, [
        ['Ion energy', '25–6,000 keV'],
        ['Electron energy', '25–250 keV'],
        ['Cadence', '16 s'],
      ]),
      card('mag', 'MAG · magnetic field', sources.solar, [
        ['Range', '±440 nT'],
        ['Cadence', '8 Hz'],
        ['Boom length', '5.6 m'],
      ]),
      card('ccor', 'CCOR-2 · solar corona', sources.solar, [
        ['Field of view', '3.0–23.5 solar radii'],
        ['Cadence', '15 min'],
        ['Resolution', '65 arcsec'],
      ]),
    ];
  if (entry.id === 'goes19')
    return [
      card('ccor', 'CCOR-1 · solar corona', sources.solar, [
        ['Field of view', '3.7–18.7 solar radii'],
        ['Cadence', '15 min'],
        ['Resolution', '33 arcsec'],
      ]),
    ];
  if (entry.id === 'noaa20')
    return [
      card('ceres', 'CERES FM6 · radiation budget', sources.ceres, [
        ['Nadir footprint', '24 km'],
        ['Longwave channel', '5–35 μm'],
        ['Measurement', 'Reflected solar and emitted terrestrial radiation'],
      ]),
    ];
  if (entry.id === 'gazelle')
    return [
      card('argos', 'Argos-4 · hosted data collection', sources.argos, [
        ['Host', 'GAzelle / Orbital Test Bed-3'],
        ['Launch', '7 October 2022'],
        ['Measurement', 'Messages from mobile and stationary environmental transmitters'],
      ]),
    ];
  return [];
}
